import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Plus, Trash2, ChevronLeft } from 'lucide-react';
import { useIntelliHealthChat } from '@/contexts/IntelliHealthChatContext';

interface ConversationSidebarProps {
  isOpen: boolean;
  onClose: () => void;
}

const ConversationSidebar: React.FC<ConversationSidebarProps> = ({ isOpen, onClose }) => {
  const {
    conversations,
    currentConversationId,
    conversationsHasMore,
    conversationsLastEvaluatedKey,
    switchConversation,
    createConversation,
    deleteConversation,
    listConversations,
  } = useIntelliHealthChat();

  const handleSwitch = (conversationId: string) => {
    switchConversation(conversationId);
    onClose();
  };

  const handleNew = () => {
    createConversation();
    onClose();
  };

  const handleDelete = (e: React.MouseEvent, conversationId: string) => {
    e.stopPropagation();
    deleteConversation(conversationId);
  };

  const handleLoadMore = () => {
    if (conversationsLastEvaluatedKey) {
      listConversations(conversationsLastEvaluatedKey);
    }
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          initial={{ x: '-100%', opacity: 0 }}
          animate={{ x: 0, opacity: 1 }}
          exit={{ x: '-100%', opacity: 0 }}
          transition={{ type: 'spring', stiffness: 400, damping: 35 }}
          className="absolute inset-0 z-10 bg-white flex flex-col"
        >
          {/* Sidebar header */}
          <div className="flex items-center justify-between px-3 py-3 border-b border-slate-200">
            <div className="flex items-center gap-2">
              <button
                onClick={onClose}
                className="w-7 h-7 rounded-md flex items-center justify-center text-slate-500 hover:bg-slate-100 transition-colors"
                aria-label="Back to chat"
              >
                <ChevronLeft className="h-4 w-4" />
              </button>
              <h3 className="text-sm font-semibold text-slate-900">Conversations</h3>
            </div>
            <button
              onClick={handleNew}
              className="inline-flex items-center gap-1.5 rounded-lg px-2.5 py-1.5 text-xs font-medium bg-brand-teal text-white hover:bg-brand-teal/90 transition-colors"
            >
              <Plus className="h-3.5 w-3.5" />
              New Chat
            </button>
          </div>

          {/* Conversation list */}
          <div className="flex-1 overflow-y-auto">
            {conversations.length === 0 ? (
              <div className="flex items-center justify-center h-full text-sm text-slate-400">
                No conversations yet
              </div>
            ) : (
              <div className="py-1">
                {conversations.map((conv) => {
                  const isActive = conv.conversationId === currentConversationId;
                  return (
                    <button
                      key={conv.conversationId}
                      onClick={() => handleSwitch(conv.conversationId)}
                      className={`w-full flex items-center gap-2 px-3 py-2.5 text-left text-sm transition-colors group ${
                        isActive
                          ? 'bg-brand-teal-light border-l-2 border-brand-teal text-slate-900'
                          : 'text-slate-600 hover:bg-slate-50 border-l-2 border-transparent'
                      }`}
                    >
                      <span className="flex-1 truncate">
                        {conv.title || 'Untitled conversation'}
                      </span>
                      {!isActive && (
                        <button
                          onClick={(e) => handleDelete(e, conv.conversationId)}
                          className="opacity-0 group-hover:opacity-100 w-6 h-6 rounded flex items-center justify-center text-slate-400 hover:text-red-500 hover:bg-red-50 transition-all flex-shrink-0"
                          aria-label="Delete conversation"
                        >
                          <Trash2 className="h-3.5 w-3.5" />
                        </button>
                      )}
                    </button>
                  );
                })}
              </div>
            )}

            {/* Load more */}
            {conversationsHasMore && (
              <div className="px-3 py-2 border-t border-slate-100">
                <button
                  onClick={handleLoadMore}
                  className="w-full text-xs text-brand-teal hover:text-brand-teal/80 font-medium py-1.5 transition-colors"
                >
                  Load more conversations
                </button>
              </div>
            )}
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default ConversationSidebar;
